import { Post } from '../types/Post';
import frontMatter from 'front-matter';

// Import all markdown files
import * as postFiles from './postFiles';

interface PostAttributes {
  title: string;
  date: string;
  category: string;
  tags: string[];
  excerpt: string;
  featuredImage?: string;
  series?: {
    name: string;
    order: number;
  };
}

const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date');
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

const loadPosts = (): Post[] => {
  const posts: Post[] = [];

  Object.entries(postFiles).forEach(([filename, content]) => {
    try {
      // Convert camelCase to kebab-case for the slug
      const slug = filename
        .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
        .toLowerCase();

      // Parse the frontmatter
      const { attributes, body } = frontMatter<PostAttributes>(content);

      posts.push({
        slug,
        title: attributes.title || '',
        date: formatDate(attributes.date),
        category: attributes.category || '',
        tags: attributes.tags || [],
        excerpt: attributes.excerpt || '',
        featuredImage: attributes.featuredImage,
        content: body.trim(),
        series: attributes.series
      });
    } catch (error) {
      console.error(`Error loading post ${filename}:`, error);
    }
  });

  return posts.sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return isNaN(dateA) || isNaN(dateB) ? 0 : dateB - dateA;
  });
};

const posts = loadPosts();

export { posts };

export const getPost = (slug: string): Post | undefined => {
  return posts.find(post => post.slug === slug);
};

export const getPosts = (): Post[] => {
  return posts;
};

export const getPostsByCategory = (category: string): Post[] => {
  return posts.filter(post => post.category.toLowerCase() === category.toLowerCase());
}; 